import type { ReactNode } from 'react'
import { Button, Group, Text, Tooltip, type GroupProps } from '@mantine/core'

interface Icd10Choice {
  code: string
  display: string
}

/**
 * The billable codes under a non-billable ICD-10-CM category, offered as one-click replacements (PD-2014).
 * `wrapLabels` lets a chip grow to more lines instead of truncating, so the part of the label that tells
 * siblings apart (laterality) stays visible in narrow columns. `leading` sits first in the same wrapping
 * row, so it flows with the chips instead of standing alone on its own line.
 */
export function Icd10ReplaceChoices({
  choices,
  onPick,
  wrapLabels = false,
  leading,
  ...groupProps
}: {
  choices: Icd10Choice[]
  onPick: (choice: Icd10Choice) => void
  wrapLabels?: boolean
  leading?: ReactNode
} & Omit<GroupProps, 'children'>) {
  return (
    <Group gap={6} wrap="wrap" {...groupProps}>
      {leading}
      <Text size="xs" c="dimmed">
        Replace with:
      </Text>
      {choices.map((child) => (
        <Tooltip
          key={child.code}
          label={child.display}
          withArrow
          events={{ hover: true, focus: true, touch: false }}
        >
          <Button
            size="xs"
            variant="default"
            maw={320}
            aria-label={`${child.code} ${child.display}`}
            onClick={() => onPick(child)}
            // Mantine fixes the Button height and keeps the label on one line; a single-line chip
            // still renders at the xs height through min-height.
            styles={
              wrapLabels
                ? {
                    root: {
                      height: 'auto',
                      minHeight: 'var(--button-height)',
                      paddingBlock: 4,
                      display: 'inline-flex',
                      alignItems: 'center',
                    },
                    label: { whiteSpace: 'normal' },
                  }
                : undefined
            }
          >
            <Text span size="xs" truncate={!wrapLabels}>
              {child.code} · {child.display}
            </Text>
          </Button>
        </Tooltip>
      ))}
    </Group>
  )
}
